import { StatsigClient as SDKClient } from "@statsig/js-client";

import { ConfigKey, CONFIGS } from "./configs";
import { GateKey, GATES } from "./gates";
import {
  BuiltUser,
  EMPTY_READER,
  SESSION_COOKIE,
  state,
  StatsigGateContextOptions,
  StatsigReader,
  StatsigSdkUser,
  StatsigUserContext,
  UserStatus,
} from "./types";

function getRuntimeEnv(): Record<string, string> {
  if (globalThis.window === undefined) {
    return {};
  }

  return globalThis.env || {};
}

function isEnabled(): boolean {
  const env = getRuntimeEnv();
  return Boolean(env.ENABLE_STATSIG && env.STATSIG_CLIENT_KEY);
}

function resolveTier(raw?: string): string {
  if (
    globalThis.window !== undefined &&
    globalThis.location?.hostname?.includes("localhost")
  ) {
    return "localhost";
  }

  if (!raw) {
    return "development";
  }

  switch (raw.toLowerCase()) {
    case "dev":
    case "testing":
      return "development";
    case "stage":
      return "staging";
    case "prod":
      return "production";
    default:
      return "development";
  }
}

async function init(user?: StatsigUserContext) {
  state.isReady = false;
  if (!isEnabled()) {
    return;
  }

  if (state.initialized) {
    if (user) {
      const client = ensureClient();
      if (client) {
        const details = buildUser(user);
        await applyUser(client, details);
      }
    }
    return;
  }

  const details = buildUser(user);
  const client = ensureClient(details);
  if (!client) {
    return;
  }

  if (!state.initPromise) {
    state.initPromise = client
      .initializeAsync()
      .then(() => {
        state.initialized = true;
        state.isReady = true;
      })
      .catch((error) => {
        console.error("Error initializing statsig:", error);
        shutdown();
        throw error;
      });
  }

  try {
    await state.initPromise;
  } catch (error) {
    console.error("Statsig init failure:", error);
    throw error;
  }

  if (user) {
    await applyUser(client, details);
  }
}

function buildUser(context?: StatsigUserContext): BuiltUser {
  const env = getRuntimeEnv();
  const tier = resolveTier(env.CISCO_U_ENVIRONMENT);

  const ccoId = context?.ccoId;
  const email = context?.email;
  const userID = context?.userID;
  const sessionId = context?.sessionId ?? getCookie(SESSION_COOKIE);
  const isAuthenticated = userID && email ? "true" : "false";

  const custom: Record<string, string> = {};

  if (ccoId) {
    custom.ccoId = ccoId;
  }

  custom.environment = tier;
  custom.isAuthenticated = isAuthenticated;

  if (sessionId) {
    custom.sessionId = sessionId;
  }

  const user: StatsigSdkUser = {
    userID: userID ? String(userID) : sessionId,
    email: email ?? undefined,
    custom: Object.keys(custom).length ? custom : undefined,
  };

  return {
    user,
    tier,
    sessionId,
  };
}

// Maximum time (ms) waitForClientReady() will block before giving up and
// letting the caller proceed with whatever value the SDK currently has
// (typically the fallback). Prevents an indefinite hang if Statsig's
// initializeAsync() or updateUserAsync() never settles (e.g. network stall).
const CLIENT_READY_TIMEOUT_MS = 3000;

async function waitForClientReady() {
  const deadline = Date.now() + CLIENT_READY_TIMEOUT_MS;

  // Wait for the initial initializeAsync() to complete.
  while (state.isReady !== true) {
    if (state.initialized) {
      break;
    }
    if (Date.now() >= deadline) {
      console.warn(
        "Statsig waitForClientReady: timed out waiting for initial init"
      );
      return;
    }
    await new Promise((resolve) => setTimeout(resolve, 50));
  }

  // Also wait for any in-flight user update (updateUserAsync). While a user
  // update is pending, the SDK can return "Loading:Unrecognized" for gates,
  // which causes them to evaluate to their default value instead of the real
  // server value.
  while (state.userUpdatePromise) {
    const remaining = deadline - Date.now();
    if (remaining <= 0) {
      console.warn(
        "Statsig waitForClientReady: timed out waiting for user update"
      );
      return;
    }
    try {
      await Promise.race([
        state.userUpdatePromise,
        new Promise<void>((resolve) => setTimeout(resolve, remaining)),
      ]);
    } catch {
      // applyUser already logs; just stop waiting.
      break;
    }
  }
}

function ensureClient(details?: BuiltUser): SDKClient | null {
  if (state.client) {
    return state.client;
  }

  if (!isEnabled()) {
    return null;
  }

  const env = getRuntimeEnv();
  const resolved = details ?? buildUser();

  state.client = new SDKClient(env.STATSIG_CLIENT_KEY, resolved.user as any, {
    environment: { tier: resolved.tier },
  });

  state.initialized = false;
  state.initPromise = null;
  return state.client;
}

async function applyUser(client: SDKClient, details: BuiltUser) {
  const hasPrimaryId = Boolean(details.user.userID);
  const hasSessionId = Boolean(details.sessionId);

  if (!hasPrimaryId && !hasSessionId) {
    return;
  }

  const updatePromise = client
    .updateUserAsync(details.user as any)
    .then(() => {
      /* success - nothing to do */
    })
    .catch((error) => {
      console.warn("Error updating statsig user:", error);
    })
    .finally(() => {
      if (state.userUpdatePromise === updatePromise) {
        state.userUpdatePromise = null;
      }
    });

  state.userUpdatePromise = updatePromise;

  await updatePromise;
}

function logEvent(
  eventName: string,
  value?: string | number | undefined,
  metadata?: Record<string, any>
) {
  if (!state.client || !state.initialized || !state.isReady) {
    return;
  }

  try {
    const normalized = metadata
      ? Object.fromEntries(
          Object.entries(metadata).map(([key, logValue]) => [
            key,
            String(logValue),
          ])
        )
      : undefined;
    state.client.logEvent(eventName, value, normalized);
  } catch (error) {
    console.warn("Error logging statsig event:", error);
  }
}

function dynamicConfig(name: string): StatsigReader {
  const client = ensureClient();
  if (!client || !state.initialized) {
    return EMPTY_READER;
  }

  try {
    const config = client.getDynamicConfig(name);
    return (config as unknown as StatsigReader) ?? EMPTY_READER;
  } catch {
    return EMPTY_READER;
  }
}

function getConfig<T>(configKey: ConfigKey, key: string, fallback?: T): T {
  const config = CONFIGS[configKey];
  const fallbackValue = (fallback ?? config.fallback[key]) as T;

  if (!isEnabled()) {
    return fallbackValue;
  }

  const reader = dynamicConfig(config.configName);
  return reader.get(key, fallbackValue);
}

function checkGate(name: string, fallback = false): boolean {
  const client = ensureClient();
  if (!client || !state.initialized) {
    return fallback;
  }

  try {
    return client.checkGate(name);
  } catch {
    return fallback;
  }
}

async function featureGate(gateKey: GateKey): Promise<boolean> {
  const config = GATES[gateKey];

  if (!isEnabled()) {
    return config.fallback;
  }

  try {
    await waitForClientReady();
    return checkGate(config.gateName, config.fallback);
  } catch (error) {
    console.error(`Failed to evaluate ${config.gateName} gate:`, error);
    return config.fallback;
  }
}

function contextFromStatus(
  status?: UserStatus | null
): StatsigUserContext | undefined {
  if (!status) {
    return undefined;
  }

  const context: StatsigUserContext = {
    userID: status.userId,
    ccoId: status.cco_id,
    email: status.email,
  };

  return context;
}

async function resolveStatsigUserContext(
  params: StatsigGateContextOptions
): Promise<StatsigUserContext | undefined> {
  if (!Statsig.isEnabled()) {
    return undefined;
  }

  if (params.isUnauth()) {
    const sessionId = getCookie(SESSION_COOKIE);
    return sessionId ? { sessionId } : undefined;
  }

  const userContext = contextFromStatus(params.statusGetter?.());
  if (userContext) {
    return userContext;
  }

  if (params.statusGetter) {
    const ready = await waitForStatusResolution(params.statusGetter);
    if (!ready) {
      return undefined;
    }
  }

  return contextFromStatus(params.statusGetter?.());
}

async function waitForStatusResolution(
  getter: () => UserStatus | null | undefined
): Promise<boolean> {
  const start = Date.now();
  return new Promise<boolean>((resolve) => {
    const check = () => {
      const status = getter();
      if (status) {
        resolve(true);
        return;
      }

      if (Date.now() - start >= 10000) {
        resolve(false);
        return;
      }

      setTimeout(check, 50);
    };

    check();
  });
}

function getCookie(name: string): string | undefined {
  if (typeof document === "undefined") {
    return undefined;
  }

  const escaped = name.replaceAll(/([.$?*|{}()\\/+^])/g, String.raw`\$1`);
  const regex = new RegExp(`(?:^|; )${escaped}=([^;]*)`);
  const match = regex.exec(document.cookie);
  return match ? decodeURIComponent(match[1]) : undefined;
}

function shutdown() {
  try {
    state.client?.shutdown();
  } catch {}

  resetState();
}

function resetState() {
  state.client = null;
  state.initPromise = null;
  state.initialized = false;
  state.isReady = false;
  state.userUpdatePromise = null;
}

export const Statsig = {
  init,
  isEnabled,
  resolveStatsigUserContext,
  dynamicConfig,
  getConfig,
  featureGate,
  logEvent,
  shutdown,

  checkGate, // exposed for testing, please avoid using it directly, use the featureGate instead
  buildUser,
  ensureClient,
  applyUser,
  resetState,
};
